/**
 * The idle session, shared with anything below the gate that can name what is
 * currently open.
 *
 * This is deliberately its own file, holding nothing but a React context. See
 * the equivalent file in Channels and Designer for why: the gate that fills it
 * (`components/IdleSessionGate.js`) has to import `hanko-init` so it can
 * revoke the session, and anything that only wants to set the context label
 * must not drag the whole auth SDK in behind it.
 *
 * Explorer has no unsaved work to rescue — it only browses what is already
 * saved — so unlike Channels and Designer this file carries no draft-saver
 * hook, only the context label.
 */

import { createContext, useContext, useEffect } from 'react';

export const IdleContext = createContext(null);

/**
 * Returns null when there is no gate above.
 *
 * That happens in real cases: a component rendered on its own in a test, a
 * local build with no session, and any screen outside the gate. So callers
 * must cope with null rather than assume a session is there.
 */
export function useIdle() {
    return useContext(IdleContext);
}

/**
 * Name what this tab currently has open, so its own lock screen can say what
 * it was instead of just that it happened.
 *
 *   useContextLabel(surveyName);
 *
 * Pass `null` (or nothing) when there is nothing open worth naming.
 * Does nothing when there is no gate above.
 */
export function useContextLabel(label) {
    const idle = useIdle();
    const setLabel = idle && idle.setContextLabel;
    useEffect(() => {
        if (!setLabel) return undefined;
        setLabel(label || null);
        return undefined;
    }, [setLabel, label]);
}

export default IdleContext;
